import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Table, notification } from "antd";
import { ColumnsType } from "antd/es/table/Table";

import { AssessmentTestsHttpService, ITestData } from "@/shared/services/assessments/assessment-tests-http.service";
import { formatTableData } from "@/shared/utils/formaters/uiFormaters/tableFormaters";
import { shortFormatDate, longFormatDate } from "@/shared/utils/formaters/dataFormater/formatDate";
import TableHeader from "@/shared/components/TableHeader/TableHeader";
import { ConfirmResetModal } from "./components/ConfirmResetModal/ConfirmResetModal";
import { useGetAssessmentInviteHook } from "@/modules/assessments/hooks/use-get-assessment-invite.hook";

import style from "./style.module.css";
import { Button, ButtonColorType, ButtonSize } from "@/ui-components/commonComponents/Button/Button";
import { AssessmentTestStatus } from "../AssessmentInvites/invites.constants";
import { DEFAULT_LOCALE } from "@/shared/utils/locale.utils";

interface ITestTableData extends ITestData {
  key: number;
}

export function AssessmentTestResets() {
  const {
    client_id: clientId,
    assessment_id: assessmentId,
    invite_id: inviteId,
  } = useParams<{
    client_id: string;
    assessment_id: string;
    invite_id: string;
  }>();
  const navigate = useNavigate();

  const [tests, setTests] = useState<ITestTableData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTestIds, setSelectedTestIds] = useState<number[]>([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const { assessmentInvite } = useGetAssessmentInviteHook(Number(inviteId));

  const fetchTests = useCallback(async () => {
    if (!inviteId) return;

    setIsLoading(true);
    try {
      const response = await AssessmentTestsHttpService.getInviteTests(Number(inviteId));
      const testsWithKeys = response.tests.map((test) => ({
        ...test,
        key: test.testId,
      }));
      setTests(testsWithKeys);
    } catch (error) {
      notification.error({ message: "Failed to fetch tests" });
      console.error("Error fetching tests:", error);
    } finally {
      setIsLoading(false);
    }
  }, [inviteId]);

  useEffect(() => {
    fetchTests();
  }, [inviteId, fetchTests]);

  const handleResetSelected = () => {
    if (selectedTestIds.length === 0) {
      notification.error({ message: "Please select at least one test to reset" });
      return;
    }

    setIsConfirmModalOpen(true);
  };

  const handleConfirmReset = async () => {
    if (!inviteId || !clientId || !assessmentId) return;

    setIsResetting(true);
    try {
      await AssessmentTestsHttpService.resetSelectedTests(Number(inviteId), selectedTestIds);

      const selectedTestNames = getSelectedTestNames();
      const testNamesText = selectedTestNames.length === 1 ? selectedTestNames[0] : selectedTestNames.join(", ");

      let message = "";
      if (selectedTestNames.length > 1) {
        message = `The tests ${testNamesText} have been reset.`;
      } else {
        message = `The test ${testNamesText} has been reset.`;
      }

      notification.success({
        message,
        description: "The candidate will receive an email inviting them to complete the test(s).",
      });

      setSelectedTestIds([]);
      setIsConfirmModalOpen(false);

      // Navigate back to the invites page
      navigate(`/clients/${clientId}/assessments/${assessmentId}/invites`);
    } catch (error) {
      notification.error({ message: "Failed to reset selected tests" });
      console.error("Error resetting tests:", error);
    } finally {
      setIsResetting(false);
    }
  };

  const handleCancelReset = () => {
    setIsConfirmModalOpen(false);
  };

  const getSelectedTestNames = () => {
    return tests.filter((test) => selectedTestIds.includes(test.testId)).map((test) => test.testName);
  };

  const getStatusDisplay = (status: string) => {
    return AssessmentTestStatus[status as keyof typeof AssessmentTestStatus] || status;
  };

  const columns: ColumnsType<ITestTableData> = [
    {
      key: "testName",
      title: "Test Name",
      dataIndex: "testName",
      className: style.tableColumn,
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      className: style.tableColumn,
      render: (status: string) => getStatusDisplay(status),
    },
    {
      key: "startedOn",
      title: "Started On",
      dataIndex: "startedOn",
      className: style.tableColumn,
      render: (value) => formatTableData(value, shortFormatDate, longFormatDate, "-"),
    },
    {
      key: "finishedOn",
      title: "Finished On",
      dataIndex: "finishedOn",
      className: style.tableColumn,
      render: (value) => formatTableData(value, shortFormatDate, longFormatDate, "-"),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedTestIds,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedTestIds(selectedRowKeys.map(Number));
    },
  };

  return (
    <div className="main-container">
      {assessmentInvite?.claimedByUser && (
        <div className={style.userInfo}>
          <p className={style.userName}>{assessmentInvite.claimedByUser.fullName}</p>
          <p className={style.userEmail}>
            {assessmentInvite.claimedByUser.fullName} ({assessmentInvite.claimedByUser.email})
          </p>
        </div>
      )}
      <div className={style.tableBlock}>
        <TableHeader
          tableName={assessmentInvite?.assessment.name[DEFAULT_LOCALE] || "Assessment"}
          hasSearchField={false}
        >
          <Button
            onClick={handleResetSelected}
            disabled={selectedTestIds.length === 0}
            colorType={ButtonColorType.dark}
            buttonSize={ButtonSize.small}
          >
            Reset Selected
          </Button>
        </TableHeader>
        <Table
          columns={columns}
          dataSource={tests}
          loading={isLoading}
          rowSelection={rowSelection}
          pagination={false}
          className={style.testTable}
        />
      </div>

      <ConfirmResetModal
        isOpen={isConfirmModalOpen}
        isLoading={isResetting}
        onCancel={handleCancelReset}
        onConfirm={handleConfirmReset}
        selectedTestNames={getSelectedTestNames()}
      />
    </div>
  );
}
